import { prefersReducedMotion } from '@theme/utilities';
import { blobPath, shapePath, SHAPES } from '@theme/blob-shapes';

const ENERGY_EASE = 0.08; // how fast hover "heats up" / cools down
const ENERGY_SPEED = 1.3; // hover advances the wobble clock up to (1 + this)x
const MAX_DT = 50; // clamp frame delta so a backgrounded tab can't jump the clock
const FRAME_MS = 1000 / 30; // throttle path rebuilds — slow drift reads the same at 30fps
const SEED_MS = 1733; // per-blob clock offset so siblings never wobble in sync

/**
 * Field of decorative lava-lamp blobs for the footer. Each blob drifts
 * continuously (like a closed accordion row) and energizes on hover; the
 * element also writes `--p` (0..1 entry progress into the viewport) every
 * frame, which the stylesheet maps to each blob's parallax drift. Static under
 * reduced motion, paused while off-screen.
 */
class FooterBlobField extends HTMLElement {
  /** @type {{ el: Element, path: SVGPathElement, shape: string, clock: number, energy: number, hover: boolean }[]} */
  #blobs = [];
  #lastTime = 0;
  #lastPaint = 0;
  /** @type {number | null} */ #raf = null;
  /** @type {IntersectionObserver | null} */ #observer = null;
  #controller = new AbortController();

  connectedCallback() {
    const fallbackShape = Object.keys(SHAPES)[0] ?? 'petal-square';
    this.#blobs = [...this.querySelectorAll('[data-shape]')].flatMap((el, index) => {
      const path = el.querySelector('path');
      if (!(path instanceof SVGPathElement)) return [];
      const requested = el.getAttribute('data-shape') ?? '';
      const shape = SHAPES[requested] ? requested : fallbackShape;
      // Paint the wobble-free base shape immediately to avoid an empty-path flash.
      path.setAttribute('d', shapePath(shape, 0));
      return [{ el, path, shape, clock: index * SEED_MS, energy: 0, hover: false }];
    });
    // Fresh controller per connect — the aborted one from a previous
    // disconnect (editor section reorder) would silently drop listeners.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Applies regardless of reduced motion: under the curtain reveal a focused
    // control can sit invisibly behind the page — lift the curtain for it.
    this.addEventListener('focusin', this.#onFocusIn, { signal });

    if (this.#blobs.length === 0) return;

    if (prefersReducedMotion()) {
      this.style.setProperty('--p', '1');
      return;
    }

    // Set the real entry progress before the first tick so a footer already
    // in view at load doesn't flash at rest and snap down by its drift.
    this.#setProgress();
    for (const blob of this.#blobs) {
      // Wrappers are pointer-events: none; only the blob's path is a hit
      // target, so enter/leave track the drawn shape, not its bounding box.
      blob.el.addEventListener('pointerenter', () => { blob.hover = true; }, { signal });
      blob.el.addEventListener('pointerleave', () => { blob.hover = false; }, { signal });
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '120px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stop();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    // A queued entry can be delivered after the element is detached (editor
    // section rebuild) — starting the loop then would leak a rAF forever.
    if (!this.isConnected) return;
    // Entries are chronological — act on the latest state, or a fast in-and-out
    // fling ([true, false] in one batch) leaves the loop running off-screen.
    const latest = entries[entries.length - 1];
    if (latest?.isIntersecting) this.#start();
    else this.#stop();
  };

  /** Scroll to the page end so a keyboard-focused footer control is visible. */
  #onFocusIn = () => {
    const wrapper = this.parentElement;
    if (!wrapper || getComputedStyle(wrapper).position !== 'sticky') return;
    const scroller = document.scrollingElement;
    if (scroller) scroller.scrollTo({ top: scroller.scrollHeight });
  };

  /** Entry progress: 0 at first viewport contact, 1 once fully scrolled in. */
  #setProgress() {
    const rect = this.getBoundingClientRect();
    // Layout viewport, not innerHeight — mobile toolbar collapse changes
    // innerHeight mid-scroll and would pop the parallax; clientHeight is
    // stable and consistent with the svh-based footer height.
    const viewport = document.documentElement.clientHeight;
    let edge = rect.top;

    // Under the curtain reveal the wrapper is pinned, so rect.top is constant
    // for the whole scroll — track the covering content's bottom edge (the
    // curtain) instead. Skip zero-height siblings like injected scripts.
    const wrapper = this.parentElement;
    if (wrapper && getComputedStyle(wrapper).position === 'sticky') {
      let curtain = wrapper.previousElementSibling;
      while (curtain && curtain.getBoundingClientRect().height === 0) {
        curtain = curtain.previousElementSibling;
      }
      if (curtain) edge = curtain.getBoundingClientRect().bottom;
    }

    const travel = Math.min(rect.height, viewport) || 1;
    const progress = Math.min(1, Math.max(0, (viewport - edge) / travel));
    this.style.setProperty('--p', progress.toFixed(4));
    return progress;
  }

  #start() {
    if (this.#raf) return;
    this.#lastTime = 0;
    this.#lastPaint = 0;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
    this.#lastTime = 0;
    this.#lastPaint = 0;
  }

  /** @param {number} now */
  #tick = (now) => {
    const dt = this.#lastTime ? Math.min(now - this.#lastTime, MAX_DT) : 16;
    this.#lastTime = now;

    // Written every frame — the parallax transform needs scroll-rate updates,
    // unlike the throttled path drift.
    const progress = this.#setProgress();

    // A pinned footer intersects the viewport for the whole page life, so the
    // observer alone can't gate the work — while fully covered by the curtain
    // (or still below the viewport), keep only this cheap poll running.
    if (progress === 0) {
      this.#lastPaint = 0;
      this.#raf = requestAnimationFrame(this.#tick);
      return;
    }

    const paint = now - this.#lastPaint >= FRAME_MS;
    if (paint) this.#lastPaint = now;

    for (const blob of this.#blobs) {
      blob.energy += ((blob.hover ? 1 : 0) - blob.energy) * ENERGY_EASE;
      blob.clock += dt * (1 + blob.energy * ENERGY_SPEED);
      if (paint) {
        blob.path.setAttribute('d', blobPath(blob.shape, { clock: blob.clock, energy: blob.energy }));
      }
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('footer-blob-field')) {
  customElements.define('footer-blob-field', FooterBlobField);
}
